import React from 'react'
import { LimitStateSelection } from '../../types'
import { ResultsDiagramType } from '../../hooks/useResultsViewState'
import { useTranslation, TranslationKey } from '@structura/i18n'
// Buckling and Dynamics are temporarily disabled in the UI

interface CombinationOption
{
  id: string
  label: string
}

interface LoadFactorRow
{
  key: string
  categoryLabel: string
  isLeading: boolean
  gamma: number
  psi: number
  factor: number
}

interface ResultsControlPanelProps
{
  hasResult: boolean
  analysisSucceeded: boolean
  analysisFailed: boolean
  analysisErrorDetail?: string
  onAnalyzeAll: () => Promise<void>
  analyzingAll: boolean
  onAnalyzeTimoshenko?: () => Promise<void>
  analyzingTimoshenko?: boolean
  analysisTheory?: 'EulerBernoulli' | 'Timoshenko'
  selectedLimitState: LimitStateSelection
  setSelectedLimitState: (value: LimitStateSelection) => void
  selectedCombinationId: string
  setSelectedCombinationId: (value: string) => void
  combinationOptions: CombinationOption[]
  loadFactors: LoadFactorRow[] | null
  showEnvelopeNotice: boolean
  selectedDiagram: ResultsDiagramType
  setSelectedDiagram: (diagram: ResultsDiagramType) => void
  showValues: boolean
  setShowValues: (value: boolean) => void
  showMaxMin: boolean
  setShowMaxMin: (value: boolean) => void
  valueDecimals: 2 | 4
  setValueDecimals: (value: 2 | 4) => void
  diagramScale: number
  setDiagramScale: (value: number) => void
  valueDensity: number
  setValueDensity: (value: number) => void
  valueFontSize: number
  setValueFontSize: (value: number) => void

  // Advanced Analysis Props
  calculateSecondOrderEffects?: (options?: { combinationId?: string }) => Promise<void>
  calculatingSecondOrderEffects?: boolean
  secondOrderEffectsResult?: any
  calculateDynamics?: () => Promise<void>
  calculatingDynamics?: boolean
  dynamicsResult?: any
  currentMaterial?: string
  slendernessRatio?: number
}

const diagramCards: Array<{ id: ResultsDiagramType; icon: string; titleKey: TranslationKey; descKey: TranslationKey }> = [
  { id: 'moment', icon: 'bi-bezier2', titleKey: 'beam.results.cardMomentTitle', descKey: 'beam.results.cardMomentDesc' },
  { id: 'shear', icon: 'bi-scissors', titleKey: 'beam.results.cardShearTitle', descKey: 'beam.results.cardShearDesc' },
  { id: 'displacement', icon: 'bi-arrow-down-up', titleKey: 'beam.results.cardDeflectionTitle', descKey: 'beam.results.cardDeflectionDesc' },
  { id: 'axial', icon: 'bi-arrow-left-right', titleKey: 'beam.results.cardAxialTitle', descKey: 'beam.results.cardAxialDesc' },
  { id: 'reactions', icon: 'bi-arrow-up', titleKey: 'beam.results.cardReactionsTitle', descKey: 'beam.results.cardReactionsDesc' },
  { id: 'axialDisp', icon: 'bi-arrows-move', titleKey: 'beam.results.cardAxialDispTitle', descKey: 'beam.results.cardAxialDispDesc' }
]

const ResultsControlPanel: React.FC<ResultsControlPanelProps> = ({
  hasResult,
  analysisSucceeded,
  analysisFailed,
  analysisErrorDetail,
  onAnalyzeAll,
  analyzingAll,
  onAnalyzeTimoshenko,
  analyzingTimoshenko,
  analysisTheory,
  selectedLimitState,
  setSelectedLimitState,
  selectedCombinationId,
  setSelectedCombinationId,
  combinationOptions,
  loadFactors,
  showEnvelopeNotice,
  selectedDiagram,
  setSelectedDiagram,
  showValues,
  setShowValues,
  showMaxMin,
  setShowMaxMin,
  valueDecimals,
  setValueDecimals,
  diagramScale,
  setDiagramScale,
  valueDensity,
  setValueDensity,
  valueFontSize,
  setValueFontSize,
  // Advanced Analysis Props (temporarily disabled)
  calculateSecondOrderEffects: _calculateSecondOrderEffects,
  calculatingSecondOrderEffects: _calculatingSecondOrderEffects,
  secondOrderEffectsResult: _secondOrderEffectsResult,
  calculateDynamics: _calculateDynamics,
  calculatingDynamics: _calculatingDynamics,
  dynamicsResult: _dynamicsResult,
  currentMaterial: _currentMaterial,
  slendernessRatio
}) => {
  const { t } = useTranslation()

  return (
    <div>
      {/* Beam Theory Selection - Two buttons side by side */}
      <div className="mb-3">
        <label className="form-label fw-bold">{t('beam.results.beamTheory')}</label>
        <div className="row g-2">
          <div className="col-6">
            <button
              className={`btn w-100 ${analysisTheory === 'EulerBernoulli' ? 'btn-success' : 'btn-outline-success'}`}
              onClick={onAnalyzeAll}
              disabled={analyzingAll || analyzingTimoshenko}
            >
              {analyzingAll ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1"></span>
                  <span className="d-none d-sm-inline">{t('beam.results.analyzing')}</span>
                </>
              ) : (
                <>
                  <i className="bi bi-bezier2 me-1"></i>
                  Euler-Bernoulli
                  {analysisTheory === 'EulerBernoulli' && hasResult && <i className="bi bi-check ms-1"></i>}
                </>
              )}
            </button>
          </div>
          <div className="col-6">
            <button
              className={`btn w-100 ${analysisTheory === 'Timoshenko' ? 'btn-success' : 'btn-outline-success'}`}
              onClick={onAnalyzeTimoshenko}
              disabled={analyzingAll || analyzingTimoshenko || !onAnalyzeTimoshenko}
              title={t('beam.results.timoshenkoTitle')}
            >
              {analyzingTimoshenko ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1"></span>
                  <span className="d-none d-sm-inline">{t('beam.results.analyzing')}</span>
                </>
              ) : (
                <>
                  <i className="bi bi-arrows-expand me-1"></i>
                  Timoshenko
                  {analysisTheory === 'Timoshenko' && hasResult && <i className="bi bi-check ms-1"></i>}
                </>
              )}
            </button>
          </div>
        </div>
        <small className="text-muted mt-1 d-block">
          <strong>Euler-Bernoulli:</strong> {t('beam.results.eulerDesc')}<br/>
          <strong>Timoshenko:</strong> {t('beam.results.timoshenkoDesc')}<br/>
          {_currentMaterial === 'timber' && (
            <span className="text-info d-block">{t('beam.results.timberAlwaysTimoshenko')}</span>
          )}
          {slendernessRatio != null && (
            <span className={slendernessRatio > 20 ? 'text-success' : slendernessRatio < 10 ? 'text-danger' : 'text-warning'}>
              <strong>{t('beam.results.yourBeam', { ratio: slendernessRatio.toFixed(1) })}</strong>
              {slendernessRatio > 20 && t('beam.results.slenderNote')}
              {slendernessRatio >= 10 && slendernessRatio <= 20 && t('beam.results.intermediateNote')}
              {slendernessRatio < 10 && t('beam.results.deepNote')}
            </span>
          )}
        </small>
      </div>

      {/* Status messages */}
      <div className="mb-3">
        {analysisSucceeded && analysisTheory && (
          <small className="text-success">
            <i className="bi bi-check-circle me-1"></i>
            {t('beam.results.analysisCompleted', { theory: analysisTheory === 'Timoshenko' ? 'Timoshenko' : 'Euler-Bernoulli' })}
          </small>
        )}
        {!hasResult && (
          <small className="text-muted">
            <i className="bi bi-info-circle me-1"></i>
            {t('beam.results.selectTheoryHint')}
          </small>
        )}
      </div>

      <div className="mb-3">
        <label className="form-label fw-bold">{t('beam.results.limitState')}</label>
        <select
          className="form-select"
          value={selectedLimitState}
          onChange={(event) => {
            setSelectedLimitState(event.target.value as LimitStateSelection)
            setSelectedCombinationId('envelope')
          }}
          disabled={!hasResult}
        >
          <option value="ULS">{t('beam.results.ulsOption')}</option>
          <option value="SLS_CHARACTERISTIC">{t('beam.results.slsCharacteristic')}</option>
          <option value="SLS_FREQUENT">{t('beam.results.slsFrequent')}</option>
          <option value="SLS_QUASIPERMANENT">{t('beam.results.slsQuasi')}</option>
        </select>
        {!hasResult && (
          <small className="text-muted">
            {t('beam.results.runFirstLimitStates')}
          </small>
        )}
      </div>

      {combinationOptions.length > 0 && (
        <div className="mb-3">
          <label className="form-label fw-bold">{t('beam.results.loadCombinations')}</label>
          <select
            className="form-select"
            value={selectedCombinationId}
            onChange={(event) => setSelectedCombinationId(event.target.value)}
          >
            {combinationOptions.map((option) => (
              <option key={option.id} value={option.id}>{option.label}</option>
            ))}
          </select>
        </div>
      )}

      {selectedCombinationId !== 'envelope' && loadFactors && loadFactors.length > 0 ? (
        <div className="mb-3">
          <label className="form-label fw-bold">{t('beam.results.loadCaseFactors')}</label>
          <div className="table-responsive">
            <table className="table table-sm mb-0">
              <thead>
                <tr>
                  <th scope="col">{t('beam.results.thCategory')}</th>
                  <th scope="col">{t('beam.results.thLeading')}</th>
                  <th scope="col">γ</th>
                  <th scope="col">ψ</th>
                  <th scope="col">{t('beam.results.thApplied')}</th>
                </tr>
              </thead>
              <tbody>
                {loadFactors.map((factor) => (
                  <tr key={factor.key}>
                    <td>{factor.categoryLabel}</td>
                    <td>{factor.isLeading ? t('beam.results.yes') : t('beam.results.no')}</td>
                    <td>{factor.gamma.toFixed(2)}</td>
                    <td>{factor.psi.toFixed(2)}</td>
                    <td>{factor.factor.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : selectedCombinationId === 'envelope' && showEnvelopeNotice ? (
        <div className="alert alert-info">
          {t('beam.results.envelopeNotice')}
        </div>
      ) : null}

      <hr />

      {/* Detailed Analysis (Buckling & Dynamics) temporarily disabled */}

      <div className="mb-4">
        <label className="form-label fw-bold">{t('beam.results.diagramType')}</label>
        <div className="row g-2">
          {diagramCards.map((diagram) => (
            <div key={diagram.id} className="col-6">
              <div
                className={`card h-100 cursor-pointer ${selectedDiagram === diagram.id ? 'border-primary bg-light' : 'border-secondary'}`}
                onClick={() => setSelectedDiagram(diagram.id)}
                style={{ cursor: 'pointer' }}
              >
                <div className="card-body text-center py-2">
                  <i className={`${diagram.icon} fs-4 mb-1 ${selectedDiagram === diagram.id ? 'text-primary' : 'text-muted'}`}></i>
                  <div className={`fw-bold small ${selectedDiagram === diagram.id ? 'text-primary' : 'text-muted'}`}>
                    {t(diagram.titleKey)}
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                    {t(diagram.descKey)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-4">
        <label className="form-label fw-bold">{t('beam.results.displayOptions')}</label>
        <div className="row">
          <div className="col-6">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                checked={showValues}
                onChange={(event) => setShowValues(event.target.checked)}
                id="showValues"
              />
              <label className="form-check-label" htmlFor="showValues">
                {t('beam.results.showValues')}
              </label>
            </div>
          </div>
          <div className="col-6">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                checked={showMaxMin}
                onChange={(event) => setShowMaxMin(event.target.checked)}
                id="showMaxMin"
              />
              <label className="form-check-label" htmlFor="showMaxMin">
                {t('beam.results.highlightMaxMin')}
              </label>
            </div>
          </div>
          <div className="col-12 mt-2">
            <label className="form-label">{t('beam.results.valuePrecision')}</label>
            <div className="btn-group btn-group-sm" role="group">
              <button
                className={`btn btn-outline-secondary ${valueDecimals === 2 ? 'active' : ''}`}
                onClick={() => setValueDecimals(2)}
                type="button"
              >
                {t('beam.results.dp2')}
              </button>
              <button
                className={`btn btn-outline-secondary ${valueDecimals === 4 ? 'active' : ''}`}
                onClick={() => setValueDecimals(4)}
                type="button"
              >
                {t('beam.results.dp4')}
              </button>
            </div>
          </div>
          <div className="col-12 mt-2">
            <label className="form-label">{t('beam.results.fontSize')}</label>
            <div className="btn-group btn-group-sm" role="group">
              {[8, 10, 12, 14, 16, 18].map(size => (
                <button
                  key={size}
                  className={`btn btn-outline-secondary ${valueFontSize === size ? 'active' : ''}`}
                  onClick={() => setValueFontSize(size)}
                  type="button"
                >
                  {size}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <label className="form-label fw-bold">{t('beam.results.diagramScale')}</label>
        <div className="row align-items-center">
          <div className="col-8">
            <input
              type="range"
              className="form-range"
              min="0.1"
              max="3.0"
              step="0.1"
              value={diagramScale}
              onChange={(event) => setDiagramScale(parseFloat(event.target.value))}
            />
          </div>
          <div className="col-4">
            <span className="badge bg-secondary">{diagramScale.toFixed(1)}x</span>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <label className="form-label fw-bold">{t('beam.results.valueDensity')}</label>
        <div className="row align-items-center">
          <div className="col-8">
            <input
              type="range"
              className="form-range"
              min="1"
              max="20"
              step="1"
              value={valueDensity}
              onChange={(event) => setValueDensity(parseInt(event.target.value, 10))}
            />
          </div>
          <div className="col-4">
            <span className="badge bg-info">
              {t('beam.results.everyNPts', { n: valueDensity })}
            </span>
          </div>
        </div>
        <small className="text-muted">
          {t('beam.results.valueDensityHint')}
        </small>
      </div>

      {!hasResult && (
        <div className="beam-alert-info">
          <i className="bi bi-info-circle me-2"></i>
          {t('beam.results.runFirstDiagrams')}
        </div>
      )}

      {analysisSucceeded && (
        <div className="beam-alert-success">
          <i className="bi bi-check-circle me-2"></i>
          {t('beam.results.analysisComplete')}
        </div>
      )}

      {analysisFailed && (
        <div className="alert alert-danger">
          <i className="bi bi-exclamation-circle me-2"></i>
          {t('beam.results.analysisFailedMsg')}
          {analysisErrorDetail && (
            <div className="mt-2 font-monospace" style={{ fontSize: '0.85em', whiteSpace: 'pre-wrap' }}>{analysisErrorDetail}</div>
          )}
        </div>
      )}

      {/* Modals for Buckling & Dynamics are temporarily disabled */}
    </div>
  )
}

export default ResultsControlPanel


